import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Tooltip from "components/Tooltip";

// Metal name → benchmark symbol map for reliable matching
const METAL_NAME_TO_SYMBOL = {
  'cobre':      'LME_CU',
  'aluminio':   'LME_AL',
  'zinc':       'LME_ZN',
  'plomo':      'LME_PB',
  'níquel':     'LME_NI',
  'niquel':     'LME_NI',
  'estaño':     'LME_SN',
  'estano':     'LME_SN',
  'acero inox': 'REG_INOX',
  'inox':       'REG_INOX',
  'hierro':     'REG_FE',
  'acero':      'REG_FE',
  'latón':      'REG_LAT',
  'laton':      'REG_LAT',
  'bronce':     'MAN_BRZ',
};

const RISK_THRESHOLDS = {
  age: { low: 30, medium: 60 },
  pnl: { low: -2, medium: -8 },
};

function getRiskLevel(daysInInventory, latentPnlPct) {
  if (daysInInventory > RISK_THRESHOLDS?.age?.medium || latentPnlPct < RISK_THRESHOLDS?.pnl?.medium) {
    return { level: 'HIGH', color: 'var(--color-error)', bg: 'var(--color-error)15', label: 'ALTO' };
  }
  if (daysInInventory > RISK_THRESHOLDS?.age?.low || latentPnlPct < RISK_THRESHOLDS?.pnl?.low) {
    return { level: 'MEDIUM', color: 'var(--color-warning)', bg: 'var(--color-warning)15', label: 'MEDIO' };
  }
  return { level: 'LOW', color: 'var(--color-success)', bg: 'var(--color-success)15', label: 'BAJO' };
}

const COL_TOOLTIPS = [
  { header: 'Metal', tip: null },
  { header: 'Categoría', tip: null },
  { header: 'Peso (t)', tip: null },
  {
    header: 'Exposición',
    tip: {
      title: 'Exposición (€)',
      description: 'Valor actual del lote = peso × yield × benchmark_EUR vigente. Cambia en tiempo real con el precio del metal.',
    },
  },
  {
    header: 'P&L Latente',
    tip: {
      title: 'P&L Latente',
      description: 'Beneficio o pérdida potencial del lote en este momento: (benchmark_EUR × yield − precio_compra) × peso. Verde = estás en positivo. Rojo = el mercado ha movido el precio en tu contra desde que compraste.',
    },
  },
  {
    header: 'Días',
    tip: {
      title: 'Días en Inventario',
      description: 'Días transcurridos desde que se creó el lote. A más días, mayor exposición acumulada al movimiento de precios. Superar el umbral configurado genera una alerta.',
    },
  },
  {
    header: 'Riesgo',
    tip: {
      title: 'Nivel de Riesgo',
      description: 'BAJO: P&L positivo y dentro del plazo normal. MEDIO: P&L ajustado o plazo próximo al límite. ALTO: P&L negativo o lote con antigüedad superior al umbral de alerta.',
    },
  },
  {
    header: 'Buffer (€/t)',
    tip: {
      title: 'Buffer de Riesgo Activo (€/t)',
      description: 'Buffer de riesgo calculado en el momento de crear el lote (VaR dinámico o manual). Representa la protección incorporada en el precio de compra frente a caídas de mercado.',
    },
  },
];

export default function InventoryRiskPanel({ batches, benchmarks }) {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('exposure');

  const activeBatches = batches?.filter(b =>
    ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)
  );

  const enriched = activeBatches?.map(b => {
    const weight = parseFloat(b?.weight_t) || 0;
    const purchasePrice = parseFloat(b?.actual_buy_price) || parseFloat(b?.purchase_price) || 0;
    const yieldPct = parseFloat(b?.yield_est) || 100;

    // Symbol-based lookup: 1) explicit map by lowercase metal_name, 2) batch.benchmark_symbol
    const metalKey = (b?.metal_name || '')?.toLowerCase()?.trim();
    const resolvedSymbol =
      METAL_NAME_TO_SYMBOL?.[metalKey] ||
      b?.benchmark_symbol ||
      null;

    const benchmark = resolvedSymbol
      ? benchmarks?.find(bm => bm?.symbol === resolvedSymbol)
      : null;

    // Current market value per tonne of metal content = benchmark × yield%
    const benchmarkPrice = parseFloat(benchmark?.value) || 0;
    const currentNetPrice = benchmarkPrice > 0
      ? benchmarkPrice * (yieldPct / 100)
      : purchasePrice;

    // Exposición = current market value of the batch (benchmark × yield × weight)
    const exposure = currentNetPrice * weight;

    // P&L Latente = (current net price − what we paid per tonne) × weight
    const latentPnl = (currentNetPrice - purchasePrice) * weight;
    const latentPnlPct = exposure > 0 ? (latentPnl / exposure) * 100 : 0;

    const createdAt = b?.created_at ? new Date(b.created_at) : new Date();
    const daysInInventory = Math.floor((Date.now() - createdAt?.getTime()) / (1000 * 60 * 60 * 24));

    const riskBuffer = parseFloat(b?.risk_buffer) || 0;
    const risk = getRiskLevel(daysInInventory, latentPnlPct);

    return { ...b, weight, exposure, latentPnl, latentPnlPct, daysInInventory, riskBuffer, risk };
  });

  const sorted = [...(enriched || [])]?.sort((a, b) => {
    if (sortBy === 'exposure') return b?.exposure - a?.exposure;
    if (sortBy === 'pnl') return a?.latentPnl - b?.latentPnl;
    if (sortBy === 'age') return b?.daysInInventory - a?.daysInInventory;
    if (sortBy === 'risk') {
      const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
      return order?.[a?.risk?.level] - order?.[b?.risk?.level];
    }
    return 0;
  });

  const displayed = sorted?.slice(0, 8);

  const SortBtn = ({ field, label }) => (
    <button
      onClick={() => setSortBy(field)}
      className="text-xs font-caption transition-standard"
      style={{
        color: sortBy === field ? 'var(--color-accent)' : 'var(--color-muted-foreground)',
        fontWeight: sortBy === field ? 600 : 400,
        textDecoration: sortBy === field ? 'underline' : 'none',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="card p-4 lg:p-5 min-w-0 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon name="ShieldAlert" size={18} color="var(--color-accent)" strokeWidth={2} />
          <Tooltip
            title="Panel de Riesgo de Inventario"
            description="Tabla de todos los lotes activos y en proceso, ordenados por exposición. Muestra en tiempo real el valor de cada posición y su riesgo asociado."
          >
            <h3 className="font-heading font-semibold text-base text-foreground">Riesgo de Inventario</h3>
          </Tooltip>
          <span
            className="flex items-center gap-1 text-xs font-caption px-2 py-0.5 rounded-full"
            style={{ background: 'var(--color-success)20', color: 'var(--color-success)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" style={{ background: 'var(--color-success)' }} />
            EN VIVO
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-caption text-muted-foreground">Ordenar:</span>
          <SortBtn field="exposure" label="Exposición" />
          <SortBtn field="pnl" label="P&L" />
          <SortBtn field="age" label="Antigüedad" />
          <SortBtn field="risk" label="Riesgo" />
        </div>
      </div>
      {/* Table */}
      {displayed?.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
          <Icon name="Package" size={28} color="currentColor" strokeWidth={1.5} />
          <p className="text-sm font-caption">Sin lotes activos en inventario</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-border">
                {COL_TOOLTIPS?.map(({ header, tip }) => (
                  <th key={header} className="text-left py-2 px-2 text-xs font-caption text-muted-foreground font-medium whitespace-nowrap">
                    {tip ? (
                      <Tooltip title={tip?.title} description={tip?.description}>
                        <span>{header}</span>
                      </Tooltip>
                    ) : (
                      header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed?.map((b) => (
                <tr key={b?.id} className="border-b border-border hover:bg-muted/30 transition-standard">
                  <td className="py-2.5 px-2">
                    <span className="font-caption font-medium text-foreground text-xs">{b?.metal_name || '—'}</span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span className="text-xs font-caption text-muted-foreground">{b?.category_name || b?.scrap_category || '—'}</span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span className="font-data text-xs text-foreground">{b?.weight?.toFixed(2)}</span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span className="font-data text-xs text-foreground">{b?.exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span
                      className="font-data text-xs font-semibold"
                      style={{ color: b?.latentPnl >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}
                    >
                      {b?.latentPnl >= 0 ? '+' : ''}{b?.latentPnl?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                    </span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span
                      className="font-data text-xs"
                      style={{ color: b?.daysInInventory > 60 ? 'var(--color-error)' : b?.daysInInventory > 30 ? 'var(--color-warning)' : 'var(--color-muted-foreground)' }}
                    >
                      {b?.daysInInventory}d
                    </span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span
                      className="text-xs font-caption font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: b?.risk?.bg, color: b?.risk?.color }}
                    >
                      {b?.risk?.label}
                    </span>
                  </td>
                  <td className="py-2.5 px-2">
                    <span className="font-data text-xs text-muted-foreground">
                      {b?.riskBuffer > 0 ? `${b?.riskBuffer?.toFixed(1)} €/t` : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Footer */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs font-caption text-muted-foreground">
          {activeBatches?.length > 8 ? `Mostrando 8 de ${activeBatches?.length} lotes` : `${activeBatches?.length} lotes activos`}
        </span>
        <button
          onClick={() => navigate('/inventory-management')}
          className="text-xs font-caption flex items-center gap-1 transition-standard hover:opacity-70"
          style={{ color: 'var(--color-accent)' }}
        >
          Ver todo <Icon name="ArrowRight" size={12} color="var(--color-accent)" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
